import moment from 'moment';
import { ACTIVE_LIST_KEY } from './normalize';
import { dayEventOf, dayHasMealIdeaInSlot } from './dayEvents';

/**
 * Views of the food store's entities. Each is memoized on the entity maps it reads, so it hands back
 * the same array until they change, and a component that compares props sees no change.
 */

/** How long a row checked off the active list is still shown on it, struck through. */
export const STRUCK_SECONDS = 24 * 60 * 60;

/**
 * The current minute, the clock the selectors take. A minute rather than milliseconds, so a selector
 * called on every render recomputes once a minute at most.
 */
export const nowMinute = (now = Date.now()) => Math.floor(now / 60000);

const byOrder = (a, b) => ((Number(a.order) || 0) - (Number(b.order) || 0)) || (Number(a.id) - Number(b.id));

/** Remembers the last answer for as long as every argument is the same object or value. */
const memoizeLast = (compute) => {
	let lastArgs = null;
	let lastResult;
	return (...args) => {
		if(lastArgs && args.length === lastArgs.length && args.every((arg, index) => arg === lastArgs[index])) {
			return lastResult;
		}
		lastArgs = args;
		lastResult = compute(...args);
		return lastResult;
	};
};

/** Whether a bought row was checked off within STRUCK_SECONDS of `minute`. */
export const isStruck = (row, minute) => typeof row.list_key === 'string' &&
	row.list_key.startsWith('bought:') &&
	!!row.checked_off &&
	Number(row.checked_off) >= minute * 60 - STRUCK_SECONDS;

const activeListRows = memoizeLast((groceryRows, minute) => Object.values(groceryRows)
	.filter((row) => row.list_key === ACTIVE_LIST_KEY || isStruck(row, minute))
	.map((row) => ({ ...row, struck: row.list_key !== ACTIVE_LIST_KEY }))
	.sort(byOrder));

/**
 * The active list as it is shown: its rows, and the rows checked off it in the last day still in
 * their place, with `struck` set. Checking off keeps a row's `order`, so one order sorts both. The
 * server sends a struck row once, on its bought list; this is where it is shown twice.
 */
export const selectActiveListRows = (entities, minute) => activeListRows(entities.groceryRows, minute);

const rowsByList = memoizeLast((groceryRows) => {
	const lists = {};
	Object.values(groceryRows).forEach((row) => {
		(lists[row.list_key] = lists[row.list_key] || []).push(row);
	});
	Object.values(lists).forEach((rows) => rows.sort(byOrder));
	return lists;
});

/** Every list's rows in order, by list key. */
export const selectRowsByList = (entities) => rowsByList(entities.groceryRows);

const NO_ROWS = Object.freeze([]);

/** The rows of the list with `key` ('active' or 'bought:<Y-m-d>'), in order: a selector over the entities. */
export const selectRowsOfList = (key) => (entities) => selectRowsByList(entities)[key] || NO_ROWS;

/** The active list itself, without its rows, or null while the store has none. */
export const selectActiveList = (entities) => entities.groceryLists[ACTIVE_LIST_KEY] || null;

const byNewestDay = (a, b) => String(b.purchased_on).localeCompare(String(a.purchased_on)) || (Number(b.id) - Number(a.id));

const boughtLists = memoizeLast((groceryLists, groceryRows) => {
	const rows = rowsByList(groceryRows);
	return Object.values(groceryLists)
		.filter((list) => !!list.purchased_on)
		.sort(byNewestDay)
		.map((list) => ({ ...list, rows: rows[list.key] || NO_ROWS }));
});

/**
 * Every bought list the store holds, newest first, each with its `rows` in order: what a shop
 * bought, for the shopping history and a bought list opened on its own.
 */
export const selectBoughtLists = (entities) => boughtLists(entities.groceryLists, entities.groceryRows);

/** The day, 'Y-m-d' in the browser's time, an event starts on, or null for one with no start. */
export const eventDay = (event) => (event.start_time === null || event.start_time === undefined || event.start_time === '' ?
	null :
	moment.unix(Number(event.start_time)).format('YYYY-MM-DD'));

/** A day's events in the order it shows them: all-day ones first, then by when they start. */
const byEventOrder = (a, b) => (Number(!!b.all_day) - Number(!!a.all_day)) ||
	(Number(a.start_time) - Number(b.start_time)) ||
	(Number(a.id) - Number(b.id));

const eventsByDay = memoizeLast((calendarEvents) => {
	const days = {};
	Object.values(calendarEvents).forEach((event) => {
		const day = eventDay(event);
		if(day) {
			(days[day] = days[day] || []).push(event);
		}
	});
	Object.values(days).forEach((events) => events.sort(byEventOrder));
	return days;
});

/**
 * One answer per range for as long as `calendarEvents` is the same object: the selectors below are
 * made afresh on each render, so their answers are kept here rather than in each.
 */
const perRange = (compute) => {
	let source = null;
	const answers = new Map();
	return (calendarEvents, from, to) => {
		if(calendarEvents !== source) {
			source = calendarEvents;
			answers.clear();
		}
		const key = `${from}|${to}`;
		if(!answers.has(key)) {
			answers.set(key, compute(calendarEvents, from, to));
		}
		return answers.get(key);
	};
};

const NO_EVENTS = Object.freeze([]);

const eventsInRange = perRange((calendarEvents, from, to) => {
	const days = eventsByDay(calendarEvents);
	const events = Object.keys(days)
		.filter((day) => day >= from && day <= to)
		.sort()
		.flatMap((day) => days[day]);
	return events.length ? events : NO_EVENTS;
});

const eventDaysInRange = perRange((calendarEvents, from, to) => {
	const days = eventsByDay(calendarEvents);
	const inRange = {};
	Object.keys(days).filter((day) => day >= from && day <= to).forEach((day) => {
		inRange[day] = days[day];
	});
	return inRange;
});

/**
 * The calendar events on the days `from` through `to` ('Y-m-d'), each on the day it starts, as the
 * calendar shows them: by day, and within a day all-day ones first, then by start. Plain entities,
 * with `start_time` and `end_time` in unix seconds. A selector over the entities, handing back the
 * same array until the events change.
 */
export const selectCalendarEvents = (from, to) => (entities) => eventsInRange(entities.calendarEvents, from, to);

/** selectCalendarEvents() by the day each starts on: `{ 'Y-m-d': events }`, for a grid of days. */
export const selectCalendarEventsByDay = (from, to) => (entities) => eventDaysInRange(entities.calendarEvents, from, to);

const boughtListDays = memoizeLast((groceryLists, groceryRows) => {
	const days = {};
	boughtLists(groceryLists, groceryRows).forEach((list) => {
		(days[list.purchased_on] = days[list.purchased_on] || []).push(list);
	});
	return days;
});

const NO_DAYS = Object.freeze({});

const boughtListDaysInRange = (() => {
	let source = null;
	const answers = new Map();
	return (groceryLists, groceryRows, from, to) => {
		const days = boughtListDays(groceryLists, groceryRows);
		if(days !== source) {
			source = days;
			answers.clear();
		}
		const key = `${from}|${to}`;
		if(!answers.has(key)) {
			const inRange = {};
			Object.keys(days).filter((day) => day >= from && day <= to).forEach((day) => {
				inRange[day] = days[day];
			});
			answers.set(key, Object.keys(inRange).length ? inRange : NO_DAYS);
		}
		return answers.get(key);
	};
})();

/**
 * The bought lists of the days `from` through `to` ('Y-m-d'), by the day each was bought on:
 * `{ 'Y-m-d': lists }`, each list in selectBoughtLists' shape. For the calendar's days, which show
 * what was bought on them. Hands back the same object until the lists or rows change.
 */
export const selectBoughtListsByDay = (from, to) => (entities) => boughtListDaysInRange(entities.groceryLists, entities.groceryRows, from, to);

const dayEventsByDay = memoizeLast((meals, slotNotes) => {
	const days = {};
	const on = (date) => {
		days[date] = days[date] || { meals: [], notes: [] };
		return days[date];
	};
	Object.values(meals).forEach((meal) => {
		if(meal.date) {
			on(meal.date).meals.push(meal);
		}
	});
	Object.values(slotNotes).forEach((note) => {
		if(note.date) {
			on(note.date).notes.push(note);
		}
	});
	const dayEvents = {};
	Object.keys(days).forEach((date) => {
		dayEvents[date] = dayEventOf(date, days[date].meals, days[date].notes);
	});
	return dayEvents;
});

const dayEventsInRange = perRange((byDay, from, to) => {
	const inRange = {};
	Object.keys(byDay).filter((day) => day >= from && day <= to).forEach((day) => {
		inRange[day] = byDay[day];
	});
	return Object.keys(inRange).length ? inRange : NO_DAYS;
});

/**
 * The days `from` through `to` ('Y-m-d') that have meals or slot notes, as the meal screens show them
 * (food/dayEvents.js): `{ 'Y-m-d': dayEvent }`, a day with nothing on it absent. A selector over the
 * entities, handing back the same object until the meals or slot notes change.
 */
export const selectDayEvents = (from, to) => (entities) => dayEventsInRange(dayEventsByDay(entities.meals, entities.slotNotes), from, to);

/**
 * Whether slot `type` of `date` ('Y-m-d') holds a meal made from idea `mealIdeaId`, other than the
 * meal keyed `exceptKey`, in `entities`.
 */
export const hasMealIdeaInSlot = (entities, date, type, mealIdeaId, exceptKey = null) => dayHasMealIdeaInSlot(
	selectDayEvents(date, date)(entities)[date], type, mealIdeaId, exceptKey);

/** The changes shown before their save has answered, when the meal ideas slice has none. */
const NOTHING_SHOWN_AHEAD = Object.freeze({ groups: Object.freeze({}), ingredients: Object.freeze({}) });

/** `ingredients` in the order of `ids`; ones `ids` lacks keep their place after those it has. */
const inOrderOf = (ingredients, ids) => {
	const byId = new Map(ingredients.map((ingredient) => [Number(ingredient.id), ingredient]));
	const ordered = ids.map((id) => byId.get(Number(id))).filter(Boolean);
	const placed = new Set(ordered);
	return [...ordered, ...ingredients.filter((ingredient) => !placed.has(ingredient))];
};

const mealIdeasIndex = memoizeLast((mealIdeas, mealIdeaGroups, groupOrder, shownAhead) => {
	let ideasById = mealIdeas;
	let groupsById = mealIdeaGroups;
	const groupIds = Object.keys(shownAhead.groups).filter((id) => mealIdeaGroups[id]);
	if(groupIds.length) {
		groupsById = { ...mealIdeaGroups };
		groupIds.forEach((id) => {
			groupsById[id] = { ...mealIdeaGroups[id], meal_idea_ids: shownAhead.groups[id].filter((ideaId) => mealIdeas[ideaId]) };
		});
	}
	const ideaIds = Object.keys(shownAhead.ingredients).filter((id) => mealIdeas[id]);
	if(ideaIds.length) {
		ideasById = { ...mealIdeas };
		ideaIds.forEach((id) => {
			ideasById[id] = { ...mealIdeas[id], ingredients: inOrderOf(mealIdeas[id].ingredients || [], shownAhead.ingredients[id]) };
		});
	}
	return { ideasById, groupsById, groupOrder };
});

/**
 * The meal ideas and their groups in normalizeIndex()'s shape, `{ ideasById, groupsById,
 * groupOrder }`: the food store's, with the changes the meal ideas slice shows ahead of their save
 * (a group's members, an idea's ingredient order) over them. Takes the whole state, since those live
 * in the meal ideas slice. The same object until either changes.
 */
export const selectMealIdeasIndex = (state) => {
	const entities = state.food.entities;
	const shownAhead = (state.mealIdeas && state.mealIdeas.shownAhead) || NOTHING_SHOWN_AHEAD;
	return mealIdeasIndex(entities.mealIdeas, entities.mealIdeaGroups, entities.groupOrder, shownAhead);
};

const byIdeaName = (ideasById) => (a, b) => String(ideasById[a].name).localeCompare(String(ideasById[b].name));

/** The ids of the ideas in no group, by name, from selectMealIdeasIndex()'s answer. */
export const selectUngroupedIdeaIds = (index) => {
	const grouped = new Set();
	Object.values(index.groupsById).forEach((group) => {
		group.meal_idea_ids.forEach((id) => grouped.add(id));
	});
	return Object.keys(index.ideasById)
		.map(Number)
		.filter((id) => !grouped.has(id))
		.sort(byIdeaName(index.ideasById));
};

/** A group's ideas in member order, from selectMealIdeasIndex()'s answer. */
export const selectGroupIdeas = (index, groupId) => {
	const group = index.groupsById[groupId];
	if(!group) {
		return [];
	}
	return group.meal_idea_ids.map((id) => index.ideasById[id]).filter(Boolean);
};

/** The idea open in the Meal Ideas tab's popup, or null. Takes the whole state. */
export const selectSelectedIdea = (state) => {
	const id = state.mealIdeas ? state.mealIdeas.selectedIdeaId : null;
	return id === null || id === undefined ? null : (selectMealIdeasIndex(state).ideasById[id] || null);
};

/**
 * Whether the meal ideas are there to show: the food store holds the budget's copy, or, when it
 * could not get one, a meal_idea answer has brought them (the meal ideas slice's `loaded`).
 */
export const selectMealIdeasLoaded = (state) => state.food.status === 'restored' || state.food.status === 'fresh' ||
	!!(state.mealIdeas && state.mealIdeas.loaded);

/** Whether the meal ideas are on their way with nothing yet to show: a snapshot in flight, a first one or one asked for again. */
export const selectMealIdeasLoading = (state) => !selectMealIdeasLoaded(state) && !!state.food.refreshing;

const byItemName = (a, b) => String(a.name).localeCompare(String(b.name)) || (Number(a.id) - Number(b.id));

const groceryItemCatalog = memoizeLast((groceryItems) => Object.values(groceryItems).sort(byItemName));

/**
 * The budget's grocery items, `{ id, name, kind_of_id, tags }`, by name: what the calendar's item
 * search and the item editor's kinds offer. A selector over the entities, handing back the same
 * array until the catalog changes.
 */
export const selectGroceryItemCatalog = (entities) => groceryItemCatalog(entities.groceryItems);

const monthName = (date) => moment(date, 'YYYY-MM-DD').format('MMMM YYYY');

/**
 * Bought lists (selectBoughtLists' shape) grouped under 'MMMM YYYY' headings, newest month first
 * and newest day first within a month, leaving out any list that is not a bought one.
 * @returns {Array<[string, Object[]]>}
 */
export const boughtListsByMonth = (lists) => {
	const months = new Map();
	[...lists].filter((list) => !!list.purchased_on).sort(byNewestDay).forEach((list) => {
		const name = monthName(list.purchased_on);
		if(!months.has(name)) {
			months.set(name, []);
		}
		months.get(name).push(list);
	});
	return [...months];
};

/**
 * The bought lists with a row whose name contains `query`, ignoring case, grouped as
 * boughtListsByMonth() groups them. A blank query keeps every bought list.
 */
export const searchBoughtLists = (lists, query) => {
	const needle = String(query || '').trim().toLowerCase();
	return boughtListsByMonth(needle ? lists.filter((list) => (list.rows || [])
		.some((row) => String(row.name).toLowerCase().includes(needle))) : lists);
};
